/**
 * sessionLockGuard.js — PROD-1197. What a signed-in route shows while the session PIN lock is on.
 *
 * ★★★ THE DEFECT. The lock screen was rendered only by MobileLayout, and the away/return verdict
 *   was taken only by hooks mounted in MobileLayout. /onboarding (the health-profile run,
 *   `?run=profile`), /security, /2fa-upgrade and /dev/stream are routes OUTSIDE it. On the web a
 *   person at a tab showing the PIN lock could type /onboarding?run=profile and reload: the run
 *   opened with the patient's insurance, allergies and medications, and saved changes.
 *
 * THE RULE, decided here and nowhere else (components/SessionLockGuard.jsx renders it):
 *   - not signed in                       -> 'open'     (pre-auth sign-up carries no session)
 *   - signed in and `isLocked`            -> 'locked'   (the lock screen instead of the route)
 *   - otherwise, where the route judges the away stamp itself (`judgeAway`, every route outside
 *     MobileLayout, whose own hook judges it there), the persisted `awayAt` gets the SAME verdict
 *     Home would give it (`decideOnReturn`):  lock -> 'lock', logout -> 'logout',
 *     continue -> 'continue' (clear the stamp, as Home does).
 *   - ★ PROD-1192: the away stamp is judged only once the profile says sign-up is COMPLETE
 *     (`onboardingCompleted === true`, the same flag OnboardingFlow needs before it opens the
 *     run). A patient in setup, or a profile not known yet, gets 'open' — exactly what these
 *     routes did before this guard existed, so nobody is locked or signed out of sign-up.
 *
 * Pure: `now` is required and the clock is never read here.
 */
import { decideOnReturn, isSetupInProgress, RETURN_LOCK, RETURN_LOGOUT } from './sessionReturn'

export const GUARD_OPEN = 'open'
export const GUARD_CONTINUE = 'continue'
export const GUARD_LOCKED = 'locked'
export const GUARD_LOCK = 'lock'
export const GUARD_LOGOUT = 'logout'

export function lockGuardVerdict({ isAuthenticated, isLocked, awayAt, now, user, profile, judgeAway } = {}) {
  if (isAuthenticated !== true) return GUARD_OPEN
  if (isLocked === true) return GUARD_LOCKED
  if (!judgeAway) return GUARD_OPEN
  if (awayAt === null || awayAt === undefined) return GUARD_OPEN
  if (!profile || profile.onboardingCompleted !== true) return GUARD_OPEN
  if (!Number.isFinite(now)) {
    throw new TypeError('lockGuardVerdict: `now` must be a finite epoch — this function never reads the clock')
  }
  const verdict = decideOnReturn({
    awayAt,
    now,
    pinEnabled: !!user?.sessionPinEnabled,
    pinWindowMinutes: user?.sessionPinWindow,
    setupInProgress: isSetupInProgress(profile),
  })
  if (verdict === RETURN_LOCK) return GUARD_LOCK
  if (verdict === RETURN_LOGOUT) return GUARD_LOGOUT
  return GUARD_CONTINUE
}
